/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { HttpErrorHandler } from '@src/app/shared/HttpErrorHandler';
import { OurAppTrackerService } from '@src/app/shared/our-app-tracker-service';

/* MatSnackBar shows one message at a time and opening another dismisses it, so messages are queued
 * and each is shown in turn as the one before it closes. */
@Injectable({
    providedIn: 'root'
})
export class MessageService {
    private snackBar = inject(MatSnackBar);

    oats = inject(OurAppTrackerService);

    private waiting: QueuedMessage[] = [];
    private showing: QueuedMessage = null;
    private pausingBetweenMessages = false;

    show(msgOrString: MsgDef | string, closer = 'X'): QueuedMessage {
        const msg = msgOrString instanceof MsgDef ? msgOrString : new MsgDef(msgOrString);
        this.oats.logAction(MESSAGE_STYLES[msg.type].logPrefix + msg.text);

        const repeated = this.findRepeatOfLatest(msg);
        if (repeated) {
            return repeated;
        }
        const queued = new QueuedMessage(msg, closer);
        this.waiting.push(queued);
        if (!this.showing && !this.pausingBetweenMessages) {
            this.showNext();
        }
        return queued;
    }

    private findRepeatOfLatest(msg: MsgDef): QueuedMessage {
        const latest = this.waiting.length > 0 ? this.waiting[this.waiting.length - 1] : this.showing;
        if (latest && !latest.dismissed && latest.msg.text === msg.text && latest.msg.type === msg.type) {
            return latest;
        }
        return null;
    }

    private showNext() {
        let next = this.waiting.shift();
        while (next?.dismissed) {
            next = this.waiting.shift();
        }
        if (!next) {
            return;
        }
        this.showing = next;
        const style = MESSAGE_STYLES[next.msg.type];
        const snackBarRef = this.snackBar.open(next.msg.text, next.closer,
            { duration: style.duration, verticalPosition: 'top', panelClass: style.panelClass });
        next.attach(snackBarRef);
        snackBarRef.afterDismissed().subscribe(() => this.pauseThenShowNext());
    }

    /* Without a visible gap, two similar messages back to back look like one that never changed. */
    private pauseThenShowNext() {
        this.showing = null;
        this.pausingBetweenMessages = true;
        window.setTimeout(() => {
            this.pausingBetweenMessages = false;
            this.showNext();
        }, PAUSE_BETWEEN_MESSAGES_MS);
    }

    showErrorMessage(msg: string) {
        const md = new MsgDef(msg, 'fail');
        this.show(md);
    }

    warn(msg: string) {
        const md = new MsgDef(msg, 'warn');
        this.show(md);
    }

    showError(failure: HttpErrorResponse, operation: string) {
        const h = new HttpErrorHandler();
        console.error(failure);
        const msg = h.getErrorMessage(failure, operation);
        return this.show(msg);
    }
}

type MsgType = 'success' | 'fail' | 'warn';

const PAUSE_BETWEEN_MESSAGES_MS = 500;

const MESSAGE_STYLES: Record<MsgType, { duration: number, panelClass: string, logPrefix: string }> = {
    success: { duration: 3000, panelClass: 'message-success', logPrefix: '' },
    warn: { duration: 10000, panelClass: 'message-warn', logPrefix: 'WARNING: ' },
    fail: { duration: 30000, panelClass: 'message-fail', logPrefix: 'ERROR: ' },
};

/* Returned by show() before the message may be on screen, so dismissing one still waiting in the
 * queue just drops it. */
export class QueuedMessage {
    snackBarRef: MatSnackBarRef<TextOnlySnackBar> = null;
    dismissed = false;
    readonly actionClicked = new Subject<void>();

    constructor(readonly msg: MsgDef, readonly closer: string) {}

    attach(snackBarRef: MatSnackBarRef<TextOnlySnackBar>) {
        this.snackBarRef = snackBarRef;
        snackBarRef.onAction().subscribe(() => this.actionClicked.next());
        snackBarRef.afterDismissed().subscribe(() => this.actionClicked.complete());
    }

    dismiss() {
        this.dismissed = true;
        if (this.snackBarRef) {
            this.snackBarRef.dismiss();
        } else {
            this.actionClicked.complete();
        }
    }
}

export class MsgDef {
    text: string;
    type: MsgType;
    constructor(text: string, type: MsgType = 'success') {
        this.text = text;
        this.type = type;
    }
    toString(): string {
        return this.text;
    }
}

const WARN = 'warn';

export const OLD_MESSAGES = {

    area: new MsgDef('Area'),
    areaCreated: new MsgDef('New area has been saved'),
    areaMaintenance: new MsgDef('Areas Maintenance'),
    areaTitleHeader: new MsgDef('Title'),
    areaTitleLabel: new MsgDef('Name for this area'),
    areaUpdated: new MsgDef('New area has been saved'),

    cancel: new MsgDef('Cancel'),

    carParkSpace: new MsgDef('Car Park Space'),
    carParkSpaceCreated: new MsgDef('New car park space has been saved'),
    carParkSpaceTitleHeader: new MsgDef('Title'),
    carParkSpaceTitleLabel: new MsgDef('Name for this car park space (i.e. Space Number)'),
    carParkSpaceOwningUnitLabel: new MsgDef('Unit which owns this car park space'),

    carParkSpaceUpdated: new MsgDef('New car park space has been saved'),

    commentLeaveLabel: new MsgDef($localize`Leave a comment`),
    commentReplyLabel: (to: string) => new MsgDef(`Reply to ${to}'s comment`),

    delete: new MsgDef('Delete'),
    deleteConfirm: new MsgDef('Are you sure you want to delete this object?'),
    deleteConfirmed: new MsgDef('Object has been marked deleted in database'),

    edit: new MsgDef('Edit'),

    freeFormComment: new MsgDef('Comments'),
    freeFormCommentLabel: new MsgDef('Any comment for this item'),


    mediaSaved: new MsgDef('Media file has been uploaded successfully to the server'),

    openRequest: new MsgDef('Open Request'),


    pageMenuOrderTitle: new MsgDef('Menu Position this item will appear in'),
    pageParentLabel: new MsgDef('Parent (Under which this page will display in menu)'),
    pageTitleLabel: new MsgDef('Title for this page'),
    pageTitleHint: new MsgDef('You can use " - " to seperate menu title, e.g. Menu title - remainder of title'),
    pageUpdated: new MsgDef('Page has been saved'),

    postTitleLabel: new MsgDef('Post Title'),

    save: new MsgDef('Save'),
    startDiscussion: new MsgDef('Start Discussion'),

    updatedBy: new MsgDef('Updated By'),
    updated: new MsgDef('Updated'),

    unit: new MsgDef('Unit'),
    unitAreaLabel: new MsgDef('Area that this unit is part of'),
    unitCreated: new MsgDef('New unit has been saved'),
    unitOwnerLabel: new MsgDef('User who owns and votes for this unit'),
    unitUpdated: new MsgDef('Unit has been updated successfully'),
    unitTitleHeader: new MsgDef('Title'),
    unitTitleLabel: new MsgDef('Name for this unit'),

    unauthorized: new MsgDef('', WARN),
};
